/**
 * One normalised view of the RDS migration state.
 *
 * The raw payload (GET /api/admin/rds-health/ -> RDS /ready proxied by
 * backend/api/rds_dashboard.py) is shaped by RDS, not by this app: the
 * flags carry their table, the mirror carries its own age, the source mode
 * can arrive in two places. Every layout in rdsMigration/ therefore reads
 * THIS model and never the raw payload, so no layout can drift away from
 * the others in what they claim about who is serving what.
 *
 * The one question this file exists to answer, for every row it returns:
 *   "is this piece of data coming from Supabase or from RDS right now?"
 *
 * Answer rules (derived once, here):
 *
 *   RDS unreachable            -> everything is on Supabase.
 *   RDS kill switch on         -> everything is on Supabase.
 *   Frontend build switch off  -> everything is on Supabase (this build
 *                                 never asks RDS, whatever the flags say).
 *   source_mode = independent  -> RDS owns PHI; Supabase owns sign-in,
 *                                 non-PHI columns and every write until
 *                                 the overlay forwards it.
 *   overlay_<table> effective  -> every read of that table takes its PHI
 *                                 columns from RDS, the rest from Supabase.
 *   per-call-site flag effective-> that screen's whole row comes from RDS.
 *   nothing effective          -> that table is pure Supabase.
 *
 * Nothing here reads or writes PHI: only flag names, table names, counts
 * and timestamps leave this file.
 */
import { cutoverBuildEnabled } from "../../../utils/rdsCutover";
import { formatAge } from "../../../utils/resultCache";

/** Where a piece of data is coming from. */
export const SOURCE = {
  SUPABASE: "supabase",
  RDS: "rds",
  BOTH: "both",
  NONE: "none",
};

/**
 * Colour is STATE, never identity. A source that is serving data right now
 * is GREEN; one that is not is RED; when both serve, both are green. The
 * word (SUPABASE / RDS) carries the identity, so no colour has to be
 * decoded to know which system a chip is about.
 */
export const ON_TONE = "green";
export const OFF_TONE = "red";

export const SOURCE_TONE = {
  [SOURCE.SUPABASE]: ON_TONE,
  [SOURCE.RDS]: ON_TONE,
  [SOURCE.BOTH]: ON_TONE,
  [SOURCE.NONE]: OFF_TONE,
};

/** Realtime usage windows: "right now" and "the last few minutes". */
export const USAGE_RECENT_SECONDS = 60;
export const USAGE_WINDOW_SECONDS = 300;

export const SOURCE_LABEL = {
  [SOURCE.SUPABASE]: "SUPABASE",
  [SOURCE.RDS]: "RDS",
  [SOURCE.BOTH]: "SUPABASE + RDS",
  [SOURCE.NONE]: "NO SOURCE",
};

/** RDS's own mirror freshness bound (RDS_MIRROR_MAX_AGE_SEC default). */
export const STALE_MIRROR_AFTER_SECONDS = 900;

/** How old the access/audit mirrors may get before they are stale (1 day). */
export const STALE_AFTER_SECONDS = 86400;

/** Does the payload say RDS is independent (either spelling of the field)? */
export function isIndependent(readiness) {
  return readiness?.source_mode?.mode === "independent"
    || readiness?.cutover?.source_mode === "independent";
}

/** Is RDS paused -- source mode "supabase_only" ("Use Supabase only" without
 *  mirroring)? The app then uses Supabase alone, RDS pulls nothing and keeps
 *  its data, and RDS reports the kill switch on so every switch is vetoed. */
export function isPaused(readiness) {
  return readiness?.source_mode?.mode === "supabase_only"
    || readiness?.cutover?.source_mode === "supabase_only";
}

/** RDS is unreachable at all (its liveness probe never answered). */
function unreachable(health) {
  return !health?.liveness?.reachable;
}

function ageText(ts) {
  if (!ts) return "never";
  return formatAge(ts);
}

/**
 * Group the cutover flags into one row per table (and one per storage
 * bucket). The grouping is derived from the flags themselves -- each flag
 * names its table -- so it cannot disagree with what the flags do.
 */
function groupFlags(cutover) {
  const flags = cutover?.flags || {};
  const tables = new Map();
  const buckets = new Map();

  Object.entries(flags).forEach(([name, f]) => {
    const flag = f || {};
    const isStorage = name.startsWith("storage_");
    const bucket = isStorage
      ? String(flag.table || "").replace(/^bucket:/, "") || name.replace(/^storage_/, "")
      : null;
    const table = isStorage ? bucket : (flag.table || "(no table)");

    const map = isStorage ? buckets : tables;
    let entry = map.get(table);
    if (!entry) {
      entry = { table, bucket, flags: [], overlay: null, reads: [], writes: [] };
      map.set(table, entry);
    }
    entry.flags.push({ name, ...flag });

    if (name.startsWith("overlay_")) entry.overlay = { name, ...flag };
    else if (name.startsWith("write_")) entry.writes.push({ name, ...flag });
    else if (isStorage) entry.reads.push({ name, ...flag });
    else entry.reads.push({ name, ...flag });
  });

  return { tables, buckets };
}

/**
 * One row per PHI table: where its data comes from right now, and the one
 * button that changes it.
 */
function buildTableRows(cutover, ctx) {
  const { tables } = groupFlags(cutover);
  // RDS's kill switch, or this NotesAI's own switch (local_switch): either
  // way nothing on this deployment is read from RDS.
  const killed = !!cutover?.kill_switch || !!ctx.localPaused;
  const out = [];

  tables.forEach((entry, table) => {
    const overlay = entry.overlay;
    // Nothing is served from RDS unless RDS is reachable, this build asks
    // it, and no kill switch is holding it back.
    const overlayOn = !!overlay?.effective && ctx.buildOn && !killed && ctx.reachable;
    const overlayBlocked = !!overlay?.enabled && !overlay.effective;
    const liveReads = ctx.buildOn && !killed && ctx.reachable
      ? entry.reads.filter((f) => f.effective) : [];
    const liveWrites = ctx.buildOn && !killed && ctx.reachable
      ? entry.writes.filter((f) => f.effective) : [];
    const anyEnabled = entry.flags.some((f) => f.enabled);

    // RDS holds this flag while independent (overlay_/write_ on, per-screen
    // off). A flag someone forced OFF while independent is the opposite
    // case: RDS always allows enabling, so "Turn on" stays clickable.
    const held = !!overlay && (!!overlay.forced || ctx.independent);
    const forcedOn = held && !!overlay.enabled;
    const forcedOff = held && !overlay.enabled;

    let source = SOURCE.SUPABASE;
    let phiSource = SOURCE.SUPABASE;
    let phiLabel = "SUPABASE";
    let badge = "ALL FROM SUPABASE";
    let tone = "gray";
    let detail;

    if (!ctx.reachable) {
      // RDS's health endpoint did not answer. Whatever the flags say, the
      // app cannot reach RDS, so every read falls back to Supabase.
      tone = "red";
      badge = "RDS UNREACHABLE — ALL FROM SUPABASE";
      detail = "RDS did not answer, so every read of this table falls back to Supabase";
    } else if (overlayOn) {
      source = SOURCE.BOTH;
      phiSource = SOURCE.RDS;
      phiLabel = "RDS";
      badge = "PHI FROM RDS (every read)";
      tone = "green";
      detail = "non-PHI columns still come from Supabase; PHI columns are RDS's";
    } else if (overlayBlocked) {
      // The whole-table switch is on but not working -- so the table's PHI
      // still comes from Supabase. But some SCREENS may be served from RDS
      // through their own per-screen switch, and that must be visible: a
      // pure "Supabase" chip would understate where the data is coming from.
      if (liveReads.length) {
        source = SOURCE.BOTH;
        phiSource = SOURCE.BOTH;
        phiLabel = "SUPABASE + RDS";
      }
      badge = liveReads.length
        ? `TURNED ON, NOT WORKING · RDS ON ${liveReads.length} SCREEN${liveReads.length === 1 ? "" : "S"}`
        : "TURNED ON, NOT WORKING";
      tone = "orange";
      detail = `${overlay.reason || "blocked"} — reads stay on Supabase until this clears`
        + (liveReads.length
          ? ` · ${liveReads.length} screen${liveReads.length === 1 ? "" : "s"} (${liveReads.map((f) => f.name).join(", ")}) are served from RDS right now`
          : "");
    } else if (liveReads.length) {
      source = SOURCE.BOTH;
      phiSource = SOURCE.BOTH;
      phiLabel = `RDS · ${liveReads.length} SCREEN${liveReads.length === 1 ? "" : "S"}`;
      badge = `RDS ON ${liveReads.length} CALL SITE${liveReads.length === 1 ? "" : "S"}`;
      tone = "blue";
      detail = `${liveReads.map((f) => f.name).join(", ")} — every other read of this table is Supabase`;
    } else {
      detail = `${entry.flags.length} flag${entry.flags.length === 1 ? "" : "s"} seeded, none effective`;
    }

    if (liveWrites.length > 0) {
      detail += ` · writes mirrored to RDS: ${liveWrites.map((f) => f.name).join(", ")}`;
    }
    if (ctx.independent && entry.reads.length > 0) {
      detail += ` · ${entry.reads.length} per-screen switch${entry.reads.length === 1 ? "" : "es"} off while independent`;
    }

    // The two systems, for THIS table. Supabase always serves at least the
    // non-PHI columns, so it is always on; RDS is on when the PHI half or a
    // whole screen is read from it. Green = serving, red = not.
    const rdsServes = source === SOURCE.BOTH;
    const screens = liveReads.length;
    const systems = {
      supabase: {
        key: SOURCE.SUPABASE,
        name: "SUPABASE",
        on: true,
        tone: ON_TONE,
        state: overlayOn ? "NON-PHI" : screens ? "OTHER READS" : "ALL COLUMNS",
      },
      rds: {
        key: SOURCE.RDS,
        name: "RDS",
        on: rdsServes,
        tone: rdsServes ? ON_TONE : OFF_TONE,
        state: !ctx.reachable
          ? "UNREACHABLE"
          : overlayOn
            ? "PHI"
            : screens
              ? `${screens} SCREEN${screens === 1 ? "" : "S"}`
              : overlayBlocked
                ? "BLOCKED"
                : "OFF",
      },
    };

    out.push({
      key: `table:${table}`,
      kind: "phi-table",
      table,
      source,
      phiSource,
      phiLabel,
      badge,
      tone,
      detail,
      systems,
      // A table can be partly on RDS: the PHI half from RDS, the non-PHI
      // half from Supabase. "both" is the honest answer. The right half is
      // green only when RDS actually serves (some of) the PHI.
      split: {
        left: { label: "non-PHI · SUPABASE", tone: ON_TONE, source: SOURCE.SUPABASE },
        right: {
          label: `PHI · ${phiLabel}`,
          tone: rdsServes ? ON_TONE : OFF_TONE,
          source: phiSource,
        },
      },
      enabled: !!overlay?.enabled,
      effective: !!overlay?.effective,
      forced: forcedOn ? "on" : forcedOff ? "off" : null,
      reason: overlay?.reason || null,
      toggleName: overlay?.name || null,
      toggleLabel: overlay?.enabled ? "Turn off" : "Turn on",
      // Only RDS itself can hold a switch (while it is independent). An
      // effective overlay is still the operator's to turn off -- that is
      // how a table is moved back to Supabase.
      toggleDisabled: forcedOn,
      toggleTitle: forcedOn
        ? "held on by RDS while independent"
        : forcedOff ? "forced off while independent — turning it on is allowed"
          : undefined,
      reads: entry.reads.map((f) => f.name),
      writes: entry.writes.map((f) => f.name),
      // How many of this table's screens read their whole row from RDS --
      // the count behind "RDS ON N SCREENS".
      screensOnRds: liveReads.length,
      anyEnabled,
    });
  });

  return out.sort((a, b) => a.table.localeCompare(b.table));
}

/**
 * One row per Supabase Storage bucket. Unlike a table, "on" never means
 * unverified: every download compares RDS's copy with the live object's
 * eTag first, so a stale mirror costs a fallback, never stale content.
 */
function buildBucketRows(cutover, storageMirror, shadowReads, ctx) {
  const { buckets } = groupFlags(cutover);
  const killed = !!cutover?.kill_switch || !!ctx.localPaused;
  const out = [];

  buckets.forEach((entry, bucket) => {
    const flag = entry.flags[0] || {};
    const state = storageMirror?.by_bucket?.[bucket];
    const on = !!flag.effective && ctx.buildOn && !killed && ctx.reachable;
    const blocked = !!flag.enabled && !flag.effective;
    const results = shadowReads?.[flag.name] || [];
    const servedRds = results.filter((r) => r.served === "rds").length;
    const fellBack = results.filter((r) => r.served === "supabase_fallback").length;
    const repaired = results.filter((r) => r.outcome === "mismatched" || r.outcome === "rds_missing").length;

    let badge = "ALL FROM SUPABASE";
    let tone = "gray";
    let source = SOURCE.SUPABASE;
    let detail;
    if (!ctx.reachable) {
      badge = "RDS UNREACHABLE — ALL FROM SUPABASE";
      tone = "red";
      detail = "RDS did not answer, so every download falls back to Supabase";
    } else if (on) {
      badge = "FILES FROM RDS (checked per read)";
      tone = "green";
      source = SOURCE.BOTH;
      detail = state
        ? `${state.rds_objects ?? "?"} of ${state.source_objects ?? "?"} files mirrored · ${formatBytes(state.total_bytes)}`
          + ` · synced ${state.last_synced_at ? ageText(state.last_synced_at) : "never"}`
          + (state.ok ? "" : ` · last run failed${state.error ? `: ${state.error}` : ""}`)
        : "never synced — press Sync buckets";
    } else {
      detail = state
        ? `${state.rds_objects ?? "?"} of ${state.source_objects ?? "?"} files mirrored · ${formatBytes(state.total_bytes)}`
          + ` · synced ${state.last_synced_at ? ageText(state.last_synced_at) : "never"}`
          + (state.ok ? "" : ` · last run failed${state.error ? `: ${state.error}` : ""}`)
        : "never synced — press Sync buckets";
      if (blocked) {
        badge = "TURNED ON, NOT WORKING";
        tone = "orange";
      }
    }

    const systems = {
      supabase: {
        key: SOURCE.SUPABASE,
        name: "SUPABASE",
        on: true,
        tone: ON_TONE,
        state: on ? "SOURCE + FALLBACK" : "ALL FILES",
      },
      rds: {
        key: SOURCE.RDS,
        name: "RDS",
        on,
        tone: on ? ON_TONE : OFF_TONE,
        state: !ctx.reachable ? "UNREACHABLE" : on ? "FILES" : blocked ? "BLOCKED" : "OFF",
      },
    };

    out.push({
      key: `bucket:${bucket}`,
      kind: "storage-bucket",
      table: bucket,
      bucket,
      source,
      phiSource: on ? SOURCE.RDS : SOURCE.SUPABASE,
      phiLabel: on ? "RDS" : "SUPABASE",
      badge,
      tone,
      detail,
      systems,
      blockedReason: flag.reason || null,
      enabled: !!flag.enabled,
      effective: !!flag.effective,
      forced: flag.forced ? (flag.enabled ? "on" : "off") : null,
      reason: flag.reason || null,
      toggleName: flag.name,
      toggleLabel: flag.enabled ? "Turn off" : "Turn on",
      toggleDisabled: !!flag.forced && !!flag.enabled,
      toggleTitle: flag.forced && flag.enabled ? "held on by RDS while independent" : undefined,
      files: {
        mirrored: state?.rds_objects ?? null,
        source: state?.source_objects ?? null,
        bytesText: formatBytes(state?.total_bytes),
        syncedText: state?.last_synced_at ? ageText(state.last_synced_at) : "never",
        ok: state?.ok,
        error: state?.error || null,
      },
      session: {
        servedRds,
        fellBack,
        repaired,
        total: results.length,
      },
      anyEnabled: !!flag.enabled,
    });
  });

  return out.sort((a, b) => a.bucket.localeCompare(b.bucket));
}

export function formatBytes(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "? MB";
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GB`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} MB`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} KB`;
  return `${v} B`;
}

/**
 * The flat flag list, for the "every switch" views.
 *
 * `live` is the honest verdict: a flag RDS says is effective is only
 * actually routing traffic when this build asks RDS, nothing is vetoing
 * it, and RDS is answering. A flag that is "enabled" but not live is the
 * incident signal -- reads are silently still on Supabase -- and `reason`
 * says why.
 */
function buildFlagRows(cutover, ctx) {
  const flags = cutover?.flags || {};
  return Object.entries(flags)
    .map(([name, f]) => {
      const flag = f || {};
      let category = "read";
      if (name.startsWith("overlay_")) category = "overlay";
      else if (name.startsWith("write_")) category = "write";
      else if (name.startsWith("storage_")) category = "storage";

      const live = !!flag.effective && ctx.buildOn && !ctx.killed && ctx.reachable;
      let badge;
      let tone;
      if (flag.forced) {
        badge = flag.enabled ? "FORCED ON" : "FORCED OFF";
        tone = flag.enabled ? "purple" : "red";
      } else if (live) {
        badge = "EFFECTIVE";
        tone = "green";
      } else if (flag.enabled) {
        badge = "NOT EFFECTIVE";
        tone = "orange";
      } else {
        badge = "OFF";
        tone = "gray";
      }

      // A write switch routes WRITES to RDS; the reads of that table still
      // come from Supabase until the table's own overlay is on. Saying so
      // is the difference between an honest grid and a misleading one.
      const routesWrites = category === "write";
      const why = flag.reason
        || (live
          ? (routesWrites
            ? "effective now — this table's WRITES are mirrored to RDS; its reads still come from Supabase"
            : "effective now — this switch routes to RDS")
          : flag.enabled
            ? "on, but RDS has not made it effective yet"
            : "off — nothing is routed to RDS");

      return {
        name,
        table: flag.table || "—",
        category,
        enabled: !!flag.enabled,
        effective: !!flag.effective,
        live,
        badge,
        tone,
        routesWrites,
        why,
        forced: flag.forced ? (flag.enabled ? "on" : "off") : null,
        reason: flag.reason || null,
        updatedText: flag.updated_at ? ageText(flag.updated_at) : null,
        toggleName: name,
        toggleLabel: flag.enabled ? "Disable" : "Enable",
        toggleDisabled: !!flag.forced && !!flag.enabled,
        toggleTitle: flag.forced && flag.enabled ? "held by RDS while independent" : undefined,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The precondition checklist every layout shows at the top. */
function buildChecks(health, readiness, ctx) {
  const cutover = readiness?.cutover;
  const mirror = readiness?.data_mirror;
  const maxAge = Number.isFinite(mirror?.max_age_seconds)
    ? mirror.max_age_seconds : STALE_MIRROR_AFTER_SECONDS;
  const mirrorStale = mirror
    ? (mirror.age_seconds == null || mirror.age_seconds > maxAge)
    : null;

  return [
    {
      key: "build",
      label: "This build asks RDS",
      ok: ctx.buildOn,
      tone: ctx.buildOn ? "green" : "orange",
      detail: ctx.buildOn
        ? "RDS_API_URL is set"
        : "RDS_API_URL is not set — this NotesAI never asks RDS for rows",
    },
    {
      key: "reachable",
      label: "RDS service reachable",
      ok: !!health?.liveness?.reachable,
      tone: health?.liveness?.reachable ? "green" : "red",
      detail: health?.liveness?.reachable
        ? `answered${health?.liveness?.http_status ? ` with HTTP ${health.liveness.http_status}` : ""}`
        : "no answer from the health endpoint",
    },
    {
      key: "database",
      label: "RDS database connected",
      ok: !!readiness?.database?.connected,
      tone: readiness?.database?.connected ? "green" : "red",
      detail: readiness?.database?.connected
        ? `${readiness.database.latency_ms} ms`
        : "RDS answered but its database probe failed",
    },
    {
      key: "auth",
      label: "RDS auth enforced",
      ok: !!readiness?.security?.auth_enforced,
      tone: readiness?.security?.auth_enforced ? "green" : "orange",
      detail: readiness?.security?.auth_enforced
        ? "every request evaluated as the signed-in user"
        : "auth is disabled (dev/test mode) — a release blocker in production",
    },
    ctx.localPaused ? {
      key: "kill",
      label: "RDS enabled in this NotesAI",
      ok: false,
      tone: "orange",
      detail: "RDS is switched off for this NotesAI (Use Supabase only) — every switch below is ignored here "
        + "until you press Enable RDS. RDS itself and every other server are unaffected",
    } : ctx.paused ? {
      key: "kill",
      label: "Kill switch off",
      ok: false,
      tone: "orange",
      detail: "RDS is paused (Supabase only) — every switch below is vetoed until you press Enable RDS",
    } : {
      key: "kill",
      label: "Kill switch off",
      ok: !cutover?.kill_switch,
      tone: cutover?.kill_switch ? "red" : "green",
      detail: cutover?.kill_switch
        ? "RDS_CUTOVER_DISABLED is on — every switch below is vetoed"
        : "no rollback is in force",
    },
    {
      key: "mirror",
      label: "PHI mirror fresh",
      ok: mirrorStale === null ? null : !mirrorStale,
      tone: mirrorStale === null ? "gray" : mirrorStale ? "orange" : "green",
      detail: mirrorStale === null
        ? "this RDS version does not report the data mirror"
        : mirror.suspended
          ? (ctx.paused
            ? "suspended (RDS is paused) — nothing is pulled, RDS keeps its data"
            : "suspended (RDS is independent) — age grows by design")
          : `${mirror.tables ?? 0} copies · oldest ${mirror.age_seconds == null ? "never synced" : `${mirror.age_seconds}s ago`}`
            + ` (bound ${maxAge}s)`,
    },
    {
      key: "source",
      label: "Source of truth for PHI",
      ok: ctx.independent ? "rds" : "supabase",
      tone: ctx.independent ? "green" : "orange",
      detail: ctx.independent
        ? "RDS — the whole-table mirror is stopped"
        : ctx.paused
          ? "Supabase only — RDS is paused and nothing is mirrored into it"
          : "Supabase — the mirror copies PHI into RDS",
    },
  ];
}

/** The one-line headline every layout leads with. */
function buildStatus(health, readiness, ctx, progress) {
  if (unreachable(health)) {
    return {
      key: "unreachable",
      tone: "red",
      label: "RDS UNREACHABLE",
      source: SOURCE.SUPABASE,
      title: "Everything is coming from Supabase",
      body: "RDS's own health endpoint did not answer. Every read falls back to Supabase until the RDS service answers again.",
    };
  }
  if (ctx.localPaused) {
    const rdsMode = readiness?.source_mode?.mode;
    const rdsModeWord = rdsMode === "independent" ? "independent (the source of truth for PHI)"
      : rdsMode === "supabase_only" ? "paused by its own switch"
        : rdsMode === "mirror" ? "mirror mode" : "its own mode";
    return {
      key: "local-paused",
      tone: OFF_TONE,
      label: "SUPABASE ONLY · RDS OFF IN THIS NOTESAI",
      source: SOURCE.SUPABASE,
      title: "Everything is coming from Supabase — RDS is switched off for this NotesAI",
      body: "This NotesAI reads and writes Supabase alone. RDS itself was not changed: it keeps running in "
        + `${rdsModeWord} for every other server. Press Enable RDS to use it here again.`,
    };
  }
  if (ctx.paused) {
    return {
      key: "paused",
      tone: OFF_TONE,
      label: "SUPABASE ONLY · RDS PAUSED",
      source: SOURCE.SUPABASE,
      title: "Everything is coming from Supabase — RDS is paused",
      body: "The app reads and writes Supabase alone and RDS pulls nothing, so it keeps the data it had. "
        + "Press Enable RDS to come back to it.",
    };
  }
  if (readiness?.cutover?.kill_switch) {
    return {
      key: "kill-switch",
      tone: "red",
      label: "KILL SWITCH ON",
      source: SOURCE.SUPABASE,
      title: "Everything is coming from Supabase",
      body: "RDS_CUTOVER_DISABLED on the RDS service vetoes every switch. Turn it off there to resume.",
    };
  }
  if (ctx.independent) {
    return {
      key: "independent",
      tone: "green",
      label: "RDS IS THE SOURCE OF TRUTH",
      source: SOURCE.BOTH,
      title: "PHI comes from RDS — Supabase still serves sign-in and non-PHI columns",
      body: "The whole-table mirror from Supabase has stopped and the app forwards its PHI writes to RDS. "
        + `Catch-up reconciliation is ${readiness?.source_mode?.catchup === false ? "off" : "on"}.`
        + (ctx.buildOn ? "" : " RDS_API_URL is not set on the backend, so it still reads Supabase."),
    };
  }
  if (!ctx.buildOn) {
    return {
      key: "build-off",
      tone: OFF_TONE,
      label: "THIS BUILD NEVER ASKS RDS",
      source: SOURCE.SUPABASE,
      title: "Everything is coming from Supabase",
      body: "RDS_API_URL is not set on the backend. Set it and restart; the switches below are ready when it is.",
    };
  }
  if (progress.onRds > 0 && progress.blocked === 0) {
    return {
      key: "partial",
      tone: "green",
      label: "MIGRATING",
      source: SOURCE.BOTH,
      title: `PHI for ${progress.onRds} of ${progress.total} tables is coming from RDS`,
      body: "For those tables every read in the app takes its PHI columns from RDS; ids, owners and timestamps still come from Supabase.",
    };
  }
  if (progress.blocked > 0) {
    return {
      key: "blocked",
      tone: "orange",
      label: "BLOCKED",
      source: SOURCE.SUPABASE,
      title: `${progress.blocked} table${progress.blocked === 1 ? " is" : "s are"} switched on but not working`,
      body: "RDS says those switches cannot take effect yet — the reason is on each card below. Reads stay on Supabase until they clear.",
    };
  }
  if (progress.onSupabase > 0) {
    return {
      key: "screens",
      tone: ON_TONE,
      label: "PER-SCREEN ONLY",
      source: SOURCE.BOTH,
      title: `RDS is serving ${progress.onSupabase} screen${progress.onSupabase === 1 ? "" : "s"} — everything else is Supabase`,
      body: "Per-screen switches are on but no whole-table switch is. Turn a table on below to route all of its PHI through RDS.",
    };
  }
  return {
    key: "supabase",
    tone: OFF_TONE,
    label: "NOT STARTED · RDS NOT IN USE",
    source: SOURCE.SUPABASE,
    title: "Everything is still coming from Supabase",
    body: progress.total
      ? "RDS is connected and mirrored, but no switch is on. Turn a table on below to start."
      : "RDS reports no switches yet.",
  };
}

/**
 * Build the whole model.
 *
 * @param {object} health  the raw /api/admin/rds-health/ payload
 * @param {object} [opts]
 * @param {boolean} [opts.buildOn]  override the build-time cutover flag
 *        (the layouts read it from the same module, so it matches the app)
 * @param {object}  [opts.activity] RDS's own GET /admin/activity payload
 *        (what RDS actually served, from its audit log), or null/undefined
 *        when it could not be fetched
 * @param {number}  [opts.now]      "now" in ms, for deterministic tests
 */
export function buildViewModel(health, opts = {}) {
  const nowMs = Number.isFinite(opts.now) ? opts.now : Date.now();
  const readiness = health?.readiness || null;
  const cutover = readiness?.cutover || null;
  const buildOn = opts.buildOn === undefined ? cutoverBuildEnabled() : !!opts.buildOn;
  const independent = isIndependent(readiness);
  const paused = isPaused(readiness);
  // `killed` = every RDS route is vetoed. A paused RDS reports the kill
  // switch on (that is how the app learns to stay on Supabase); `paused`
  // tells the two apart for the wording.
  const killed = !!cutover?.kill_switch;
  const reachable = !!health?.liveness?.reachable;
  // This NotesAI's own switch (backend/api/rds_local_switch.py, carried in
  // the health payload as `local_switch`). "RDS off here" is the operator's
  // choice for THIS deployment only: RDS's own row, mode and jobs are not
  // changed, so `independent` / `paused` above stay what RDS says.
  const localSwitch = health?.local_switch && typeof health.local_switch === "object" ? health.local_switch : null;
  const localPaused = !!localSwitch?.disabled;
  const ctx = { buildOn, independent, paused, killed: killed || localPaused, localPaused, reachable };

  const tables = buildTableRows(cutover, ctx);
  const buckets = buildBucketRows(cutover, readiness?.storage_mirror, health?.shadow_reads, ctx);
  const flags = buildFlagRows(cutover, ctx);

  const overlayRows = tables.filter((t) => t.toggleName);
  const onRds = overlayRows.filter((t) => t.phiSource === SOURCE.RDS).length;
  const blocked = overlayRows.filter((t) => t.tone === "orange").length;
  const total = overlayRows.length;
  // Per-screen switches: a screen that reads its whole row from RDS. A
  // table can be served this way without any whole-table switch, and RDS
  // is then in use even though no table's PHI half has moved.
  const screensOnRds = flags.filter((f) => f.category === "read" && f.live).length;

  const progress = {
    onRds,
    total,
    blocked,
    onSupabase: screensOnRds,
    screensOnRds,
    pct: total ? Math.round((100 * onRds) / total) : 0,
  };

  const status = buildStatus(health, readiness, ctx, progress);
  const checks = buildChecks(health, readiness, ctx);

  // What each side is actually doing right now.
  const supabaseRole = localPaused
    ? "everything for this NotesAI — rows, sign-in and writes — while RDS is switched off here"
    : independent
      ? "sign-in, non-PHI columns, and every write until the overlay forwards it"
      : "source of truth for every row, sign-in, and the fallback for any read RDS cannot serve";
  const rdsRole = localPaused
    ? "switched off for this NotesAI — RDS keeps its own mode and serves every other server"
    : independent
      ? "source of truth for PHI"
      : "the PHI copy — reads switch here only when a switch is on";

  // RDS is "in use" when this build can reach it, nothing is vetoing it,
  // AND something is actually being served from it -- a table moved, a
  // screen reading its whole row from RDS, or the whole service
  // independent (which makes RDS the owner of every PHI row even before a
  // per-table switch is counted). The kill switch vetoes ALL of that: with
  // the kill switch on, every read is back on Supabase, so RDS is not in
  // use.
  const rdsActive = !unreachable(health) && buildOn && !killed && !localPaused
    && (progress.onRds > 0 || progress.screensOnRds > 0 || independent);

  const sources = {
    [SOURCE.SUPABASE]: {
      key: SOURCE.SUPABASE,
      label: "SUPABASE",
      tone: ON_TONE,
      active: true,
      role: supabaseRole,
      always: true,
      details: [
        { label: "Sign-in and roles", value: "always here" },
        { label: "Non-PHI columns", value: "always here" },
        { label: "Writes", value: independent && !localPaused ? "forwarded to RDS" : "always here" },
      ],
    },
    [SOURCE.RDS]: {
      key: SOURCE.RDS,
      label: "RDS · AWS",
      tone: rdsActive ? ON_TONE : OFF_TONE,
      active: rdsActive,
      reachable: !!health?.liveness?.reachable,
      role: rdsRole,
      always: false,
      details: [
        { label: "PHI tables served", value: `${progress.onRds} of ${progress.total}` },
        { label: "Storage buckets served", value: `${buckets.filter((b) => b.source === SOURCE.BOTH).length} of ${buckets.length}` },
        { label: "Source of truth", value: independent ? "RDS" : "Supabase" },
      ],
    },
  };

  // THE answer, in two words per system: is Supabase in use, is RDS in use.
  // There is no "RDS only" at this level -- Supabase always serves sign-in
  // and the non-PHI columns -- so the dashboard is either "Supabase only"
  // (RDS red) or "both" (both green).
  const rdsOffWhy = localPaused
    ? "RDS is switched off for this NotesAI (Use Supabase only); RDS itself keeps running for every other server"
    : !reachable
      ? "RDS is not answering, so every read falls back to Supabase"
      : paused
      ? "RDS is paused (Supabase only): nothing is read from it and nothing is mirrored into it"
      : killed
        ? "the kill switch vetoes every RDS route, so every read is on Supabase"
        : !buildOn
          ? "this NotesAI never asks RDS (RDS_API_URL is not set)"
          : "no table or screen switch is on yet, so nothing is read from RDS";
  const systems = {
    mode: rdsActive ? "both" : "supabase",
    headline: rdsActive ? "USING BOTH · SUPABASE + RDS" : "USING SUPABASE ONLY",
    tone: rdsActive ? ON_TONE : OFF_TONE,
    supabase: {
      key: SOURCE.SUPABASE,
      name: "SUPABASE",
      on: true,
      tone: ON_TONE,
      state: "IN USE",
      why: supabaseRole,
    },
    rds: {
      key: SOURCE.RDS,
      name: "RDS",
      on: rdsActive,
      tone: rdsActive ? ON_TONE : OFF_TONE,
      state: rdsActive ? "IN USE" : "NOT IN USE",
      why: rdsActive ? rdsRole : rdsOffWhy,
    },
  };

  const mirror = readiness?.data_mirror || null;
  const activity = summariseActivity(health?.shadow_reads);
  const usage = summariseUsage(health?.shadow_reads, nowMs);
  const rdsActivity = summariseRdsActivity(opts.activity, nowMs);

  return {
    ok: !!readiness?.checked && !unreachable(health),
    raw: health,
    liveness: health?.liveness || null,
    readiness,
    cutover,
    buildOn,
    independent,
    // `independent` is the FACT of what RDS's source-mode row says. What
    // the operator needs to know is whether RDS is actually the one
    // serving PHI right now -- which is not the same thing when the kill
    // switch is on, the build switch is off, or RDS is unreachable.
    ownsPhi: independent && !killed && !localPaused && buildOn && reachable,
    paused,
    killed,
    // This NotesAI's own switch: RDS off HERE (not RDS's pause above).
    localPaused,
    localSwitch,
    pinned: !!readiness?.source_mode?.pinned,
    sourceMode: readiness?.source_mode || null,
    // The raw setting RDS reports, and whether catch-up is actually
    // running (it only reconciles while RDS is independent).
    catchupOn: readiness?.source_mode?.catchup !== false,
    catchupActive: independent && !killed && buildOn && reachable
      && readiness?.source_mode?.catchup !== false,
    status,
    sources,
    systems,
    usage,
    rdsActivity,
    progress,
    checks,
    tables,
    buckets,
    flags,
    storage: {
      present: buckets.length > 0,
      count: buckets.length,
      onRds: buckets.filter((b) => b.source === SOURCE.BOTH).length,
    },
    mirror: {
      present: mirror != null,
      suspended: !!mirror?.suspended,
      tables: mirror?.tables ?? 0,
      failed: mirror?.failed || [],
      ageSeconds: mirror?.age_seconds ?? null,
      ageText: mirror?.age_seconds == null ? "never synced" : `${mirror.age_seconds}s ago`,
      maxAge: Number.isFinite(mirror?.max_age_seconds) ? mirror.max_age_seconds : STALE_MIRROR_AFTER_SECONDS,
      stale: mirror ? (mirror.age_seconds == null || mirror.age_seconds > (Number.isFinite(mirror?.max_age_seconds) ? mirror.max_age_seconds : STALE_MIRROR_AFTER_SECONDS)) : null,
      lastRun: mirror?.last_run || null,
      byTable: Object.entries(mirror?.by_table || {}).map(([table, info]) => ({
        table,
        schemas: info?.schemas ?? 0,
        ageSeconds: info?.max_age_seconds ?? null,
        failed: info?.failed || [],
        neverSynced: info?.never_synced || [],
      })),
    },
    accessMirror: {
      syncedText: readiness?.access_mirror?.last_synced_at ? ageText(readiness.access_mirror.last_synced_at) : "never",
      ageSeconds: readiness?.access_mirror?.age_seconds ?? null,
      stale: (readiness?.access_mirror?.age_seconds ?? 0) > STALE_AFTER_SECONDS,
    },
    audit: {
      lastText: readiness?.audit_pipeline?.last_event_at ? ageText(readiness.audit_pipeline.last_event_at) : "no events yet",
      ageSeconds: readiness?.audit_pipeline?.age_seconds ?? null,
      stale: (readiness?.audit_pipeline?.age_seconds ?? 0) > STALE_AFTER_SECONDS,
    },
    checkedText: readiness?.checked_at ? ageText(readiness.checked_at) : "—",
    version: readiness?.version || "unknown",
    activity,
    actions: {
      // Every switch is vetoed by the kill switch and by a build with the
      // cutover flag off, so the layouts disable the buttons accordingly.
      togglesAllowed: buildOn && !killed,
      sourceModeAllowed: !readiness?.source_mode?.pinned,
      pinnedNote: "pinned by RDS_SOURCE_MODE on the service",
    },
  };
}

/** Aggregate the live read activity into a few numbers. */
function summariseActivity(shadowReads) {
  const pilots = shadowReads || {};
  let total = 0;
  let servedRds = 0;
  let fellBack = 0;
  const perPilot = [];
  Object.entries(pilots).forEach(([pilot, rows]) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return;
    total += list.length;
    const r = list.filter((x) => x.served === "rds").length;
    const f = list.filter((x) => x.served === "supabase_fallback").length;
    servedRds += r;
    fellBack += f;
    perPilot.push({ pilot, total: list.length, servedRds: r, fellBack: f });
  });

  // Tables and columns actually observed (names only, never values).
  const byTable = {};
  Object.values(pilots).forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      if (!r?.table || !Array.isArray(r.columns) || !r.columns.length) return;
      const entry = byTable[r.table] || { columns: new Set(), lastSeen: null };
      r.columns.forEach((c) => entry.columns.add(c));
      if (!entry.lastSeen || r.at > entry.lastSeen) entry.lastSeen = r.at;
      byTable[r.table] = entry;
    });
  });

  return {
    total,
    servedRds,
    fellBack,
    perPilot: perPilot.sort((a, b) => b.total - a.total),
    tablesInUse: Object.entries(byTable)
      .map(([table, { columns, lastSeen }]) => ({
        table,
        columns: Array.from(columns).sort(),
        lastSeen,
      }))
      .sort((a, b) => a.table.localeCompare(b.table)),
  };
}

/**
 * Realtime usage per system, from the backend's in-memory report log
 * (rds_shadow_log: the last 20 outcomes per screen, newest first).
 *
 * Every report says which side SERVED it: `served: "rds"` is RDS, anything
 * else (a fallback, a shadow compare the page served from Supabase, a write
 * with nothing to forward) is Supabase. A pilot whose name starts with
 * `write_` is a write, the rest are reads. Only screens with a switch
 * report at all, so Supabase's numbers are "PHI traffic that went through a
 * switch and landed on Supabase", never "every Supabase read" -- the UI
 * says so.
 *
 * `floor` is true when some pilot's whole 20-entry log fits inside the
 * window: the real count is then AT LEAST what is shown.
 */
export function summariseUsage(shadowReads, nowMs = Date.now()) {
  const pilots = shadowReads || {};
  const blank = () => ({
    reads60: 0, writes60: 0, reads300: 0, writes300: 0, total: 0,
    lastAt: null, lastAgeSec: null, lastPilot: null, lastKind: null,
  });
  const supabase = blank();
  const rds = blank();
  let floor = false;

  Object.entries(pilots).forEach(([pilot, rows]) => {
    const list = Array.isArray(rows) ? rows : [];
    const isWrite = pilot.startsWith("write_");
    let oldest = null;
    list.forEach((r) => {
      const t = Date.parse(r?.at);
      if (Number.isNaN(t)) return;
      const age = (nowMs - t) / 1000;
      if (oldest === null || t < oldest) oldest = t;
      const target = r.served === "rds" ? rds : supabase;
      target.total += 1;
      if (age <= USAGE_WINDOW_SECONDS) {
        if (isWrite) target.writes300 += 1; else target.reads300 += 1;
      }
      if (age <= USAGE_RECENT_SECONDS) {
        if (isWrite) target.writes60 += 1; else target.reads60 += 1;
      }
      if (!target.lastAt || t > Date.parse(target.lastAt)) {
        target.lastAt = r.at;
        target.lastPilot = pilot;
        target.lastKind = isWrite ? "write" : "read";
      }
    });
    if (list.length >= 20 && oldest !== null && (nowMs - oldest) / 1000 <= USAGE_WINDOW_SECONDS) {
      floor = true;
    }
  });

  [supabase, rds].forEach((s) => {
    s.lastAgeSec = s.lastAt ? Math.max(0, Math.round((nowMs - Date.parse(s.lastAt)) / 1000)) : null;
    s.lastText = s.lastAgeSec === null ? "nothing reported yet" : shortAge(s.lastAgeSec);
    s.recent = s.reads60 + s.writes60;
    s.window = s.reads300 + s.writes300;
    s.live = s.lastAgeSec !== null && s.lastAgeSec <= USAGE_WINDOW_SECONDS;
  });

  return {
    supabase,
    rds,
    floor,
    windowSeconds: USAGE_WINDOW_SECONDS,
    recentSeconds: USAGE_RECENT_SECONDS,
    reported: supabase.total + rds.total,
  };
}

/**
 * RDS's own account of what it served: GET /admin/activity aggregates
 * rds_access.audit_log over a window (every PHI read RDS serves is audited,
 * so this is the ground truth for "is RDS being used", independent of what
 * the frontend reported). Names, counts and timestamps only.
 *
 * The timeline is re-gridded to a fixed number of slots ending now, so an
 * idle minute is an empty bar rather than a missing one.
 */
export function summariseRdsActivity(activity, nowMs = Date.now()) {
  if (!activity || typeof activity !== "object") {
    return { available: false, timeline: [], events: 0, perMinute: 0 };
  }
  const totals = activity.totals || {};
  const windowSeconds = Number(activity.window_seconds) || USAGE_WINDOW_SECONDS;
  const slotSeconds = Number(activity.slot_seconds) || Math.max(1, Math.floor(windowSeconds / 48));
  const slotCount = Math.max(1, Math.round(windowSeconds / slotSeconds));
  const byT = new Map();
  (Array.isArray(activity.timeline) ? activity.timeline : []).forEach((s) => {
    if (s && Number.isFinite(Number(s.t))) byT.set(Number(s.t), s);
  });
  const nowSec = Math.floor(nowMs / 1000);
  const lastSlot = Math.floor(nowSec / slotSeconds) * slotSeconds;
  const timeline = [];
  for (let i = slotCount - 1; i >= 0; i -= 1) {
    const t = lastSlot - i * slotSeconds;
    const s = byT.get(t) || {};
    const read = Number(s.read) || 0;
    const write = Number(s.write) || 0;
    const del = Number(s.delete) || 0;
    const miss = Number(s.miss) || 0;
    const admin = Number(s.admin) || 0;
    timeline.push({
      t,
      read, write, delete: del, miss, admin,
      // PHI traffic apart from admin rows: watching this very panel writes
      // an "activity-watch" admin row, which must not look like usage.
      phi: read + write + del + miss,
      events: Number.isFinite(Number(s.events)) ? Number(s.events) : read + write + del + miss + admin,
      agoSec: nowSec - t,
    });
  }

  const tail = (Array.isArray(activity.tail) ? activity.tail : []).slice(0, 20).map((e) => ({
    id: e.id,
    ts: e.ts,
    ageSeconds: Number.isFinite(Number(e.age_seconds)) ? Number(e.age_seconds) : null,
    name: e.name,
    kind: e.kind,
    schema: e.schema || null,
    mode: e.mode,
    family: e.family,
  }));
  const newest = tail[0] || null;
  const events = Number(totals.events) || 0;
  const reads = Number(totals.read) || 0;
  const writes = Number(totals.write) || 0;
  const deletes = Number(totals.delete) || 0;
  const misses = Number(totals.miss) || 0;
  // Usage is PHI traffic. Admin rows (syncs, cutovers, and the
  // "activity-watch" row this very panel leaves) are reported but never
  // counted as RDS being used.
  const phiEvents = reads + writes + deletes + misses;
  const minutes = Math.max(1 / 60, windowSeconds / 60);

  return {
    available: true,
    windowSeconds,
    slotSeconds,
    events,
    phiEvents,
    reads,
    writes,
    deletes,
    misses,
    admin: Number(totals.admin) || 0,
    viewers: Number(totals.viewers) || 0,
    activeTables: Number(totals.active_tables) || 0,
    activeBuckets: Number(totals.active_buckets) || 0,
    perMinute: Math.round((phiEvents / minutes) * 10) / 10,
    newestAgeSec: newest ? newest.ageSeconds : null,
    newestText: newest && newest.ageSeconds !== null ? shortAge(newest.ageSeconds) : "no events in window",
    busy: phiEvents > 0,
    timeline,
    tail,
    sourceMode: activity.source_mode?.mode || null,
  };
}

/** "3 s ago" / "2 min ago" / "1 hr ago" -- for ages measured in seconds. */
export function shortAge(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

/** Labels for the storage bucket pilots, used by the activity views. */
export function storageFlagFor(bucket) {
  return `storage_${bucket}`;
}
